import { useClientCreditCardDetails } from "../../../../../Shared/StripePayment/useClientCreditCardDetails";
import LoadingOverlay from "../../../../../Shared/LoadingOverlay";
import { Form, useHookForm } from "@powerdiary/components-library/lib";

import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { useClientPortalSelector } from "../../../../../../redux";
import { selectBooking } from "../../../../bookingSlice";
import { ConfirmBookingFunction } from "../../../../hooks/useBookingConfirmation";
import { useValidateBooking } from "../../../../hooks/useValidateBooking";
import { useSingleFlightSubmit } from "../../../../hooks/useSingleFlightSubmit";
import { useStripePayment } from "../../../../../Shared/StripePayment/useStripePayment";
import { StripePaymentSource, StripeSurcharge } from "types/generated/models";
import PaymentDetails from "./PaymentDetails";
import PaymentActions from "./PaymentActions";
import PaymentForm from "./PaymentForm";
import { formBoxSx, paymentFormSchema, PaymentFormType } from "./ConfirmationPayment.helpers";
import { BookingType } from "types/appointmentBooking";

interface PaymentRequiredProps {
    totalAmount: number;
    totalFee: number;
    confirmBooking: ConfirmBookingFunction;
    paymentOptional: boolean;
    paymentRequired: boolean;
    stripeSurchargeFullAmount: StripeSurcharge | null;
    discountContent?: string | null;
    discountedFullAmount: number;
    discountedDepositAmount: number;
    getAmountWithTax: (amount: number) => number;
}

export default function ConfirmationPayment({
    totalAmount,
    totalFee,
    confirmBooking,
    paymentRequired,
    paymentOptional,
    stripeSurchargeFullAmount,
    discountContent,
    discountedFullAmount,
    discountedDepositAmount,
    getAmountWithTax,
}: PaymentRequiredProps) {
    const { provider, session, service, location, availability, confirmation } = useClientPortalSelector(selectBooking);
    const { isLoading: creditCardInfoLoading, creditCardInfo } = useClientCreditCardDetails(provider.data?.providerId);
    const [optionalPaymentEnabled, setOptionalPaymentEnabled] = useState(false);

    const [editCardMode, setEditCardMode] = useState(false);
    const {
        clientPortalConfig: { groupPaymentsMode, appointmentsPaymentsMode },
    } = useClientPortalConfigContext();

    const bookingType = session.bookingType;

    const bookingClientLog = `Booking from client portal - ${bookingType.toLowerCase()}`;

    const createAndHandlePayment = async (paymentIntentId: string, hasTotalAmount: boolean, idempotencyKey: string) => {
        const bookingClientLog = `Booking from client portal - ${bookingType.toLowerCase()}.
            paymentMode: ${bookingType === BookingType.CLIENT ? appointmentsPaymentsMode : groupPaymentsMode},
            usingSavedCard: ${!editCardMode && !!creditCardInfo},
            portalDeposit: ${service?.data?.portalDeposit},
            amount: ${service?.data.amount},
            totalAmount: ${totalAmount},
            totalFee: ${totalFee}`;
        await confirmBooking(bookingClientLog, {
            paymentIntentId,
            hasTotalAmount: hasTotalAmount,
            idempotencyKey,
        });
    };
    const timeEntry = availability.data?.timeEntry;
    const dateTime = timeEntry?.dateTimeOffset ? DateTime.fromISO(timeEntry.dateTimeOffset) : undefined;
    const appointmentBookingMetadata =
        location.data?.locationId && timeEntry?.dateTimeOffset && timeEntry?.duration && bookingType
            ? {
                  locationId: location.data.locationId,
                  appointmentDate: dateTime?.toISO({ includeOffset: false }) ?? "",
                  appointmentOffset: dateTime?.offset,
                  durationMinutes: timeEntry.duration,
                  appointmentType: bookingType,
                  notes: confirmation.data?.notes,
                  bookingClientId: confirmation.data?.client?.id,
                  appointmentId: timeEntry.appointmentId ?? undefined,
              }
            : undefined;

    const { handlePayment, savingPayment } = useStripePayment({
        creditCardInfo,
        serviceId: service.data.serviceId,
        practitionerId: provider.data.providerId,
        editCardMode,
        stripePaymentSource: StripePaymentSource.ClientPortalAppointmentBooking,
        appointmentBooking: appointmentBookingMetadata,
        handleSuccessfulPayment: createAndHandlePayment,
    });

    const { validateBooking } = useValidateBooking();

    // One latch for every submit route out of this component. Neither savingPayment nor the
    // Stripe idempotency key closes the window on their own: savingPayment is not set until
    // validateBooking resolves, and resetIdempotencyKey runs before handleSuccessfulPayment
    // (useStripePayment.tsx), so a second click landing after the reset gets a fresh key and
    // creates a second PaymentIntent.
    const { isSubmitting, runExclusive } = useSingleFlightSubmit();

    const form = useHookForm({
        defaultValues: { storeCard: true } as PaymentFormType,
        schema: paymentFormSchema,
    });

    useEffect(() => {
        if (!creditCardInfoLoading) {
            setEditCardMode(!creditCardInfo);
        }
    }, [creditCardInfo, creditCardInfoLoading]);

    const handleSubmit = (values: PaymentFormType) =>
        runExclusive(async () => {
            await validateBooking();
            const discountedAmountWithTax = getAmountWithTax(discountedFullAmount);
            await handlePayment(values, discountedAmountWithTax, true);
        });

    const handlePaymentWithAmount = (amount: number) =>
        runExclusive(async () => {
            await validateBooking();
            const values = form.getValues();
            const amountWithTax = getAmountWithTax(amount);
            await handlePayment(values, amountWithTax, discountedFullAmount === amount);
        });

    const handleBookNow = () => runExclusive(async () => confirmBooking(bookingClientLog));

    return (
        <div data-test-id="section_required_payment">
            {(creditCardInfoLoading || savingPayment) && <LoadingOverlay />}
            <Form form={form} onSubmit={handleSubmit} dataTestIdSuffix="appointment_booking_required_payment">
                <Box sx={formBoxSx}>
                    <PaymentDetails
                        service={service?.data}
                        totalAmount={totalAmount}
                        discountContent={discountContent}
                    />

                    <PaymentForm
                        paymentRequired={paymentRequired}
                        optionalPaymentEnabled={optionalPaymentEnabled}
                        editCardMode={editCardMode}
                        creditCardInfo={creditCardInfo}
                        setEditCardMode={setEditCardMode}
                        totalFee={totalFee}
                    />

                    <PaymentActions
                        paymentOptional={paymentOptional}
                        paymentRequired={paymentRequired}
                        optionalPaymentEnabled={optionalPaymentEnabled}
                        setOptionalPaymentEnabled={setOptionalPaymentEnabled}
                        handlePaymentWithAmount={handlePaymentWithAmount}
                        onBookNow={() => void handleBookNow()}
                        disabled={isSubmitting || savingPayment}
                        stripeSurchargeFullAmount={stripeSurchargeFullAmount}
                        discountedFullAmount={discountedFullAmount}
                        discountedDepositAmount={discountedDepositAmount}
                    />
                </Box>
            </Form>
        </div>
    );
}
